import React, { useState, useRef, useEffect } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import { INCOME_CATEGORIES, EXPENSE_CATEGORIES, getCurrencySymbol } from '../categories';

const FREQS = ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly', 'Semi-annually', 'Yearly'];
const ANNUAL = {
  Daily: 365,
  Weekly: 52,
  Biweekly: 26,
  Monthly: 12,
  Quarterly: 4,
  'Semi-annually': 2,
  Yearly: 1,
};

const newId = () => Math.random().toString(36).substr(2, 9);
const newDraft = () => ({ id: newId(), name: '', amount: '', type: 'expense', frequency: 'Monthly', lastOccurrence: '', category: '' });

export default function BudgetPlanner({ title, items, debtPaymentItems = [], onTitleChange, onItemsChange, currency }) {
  const sym = getCurrencySymbol(currency);
  const fmt = (n) => sym + Math.abs(Number(n)).toFixed(2);
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';
  const { t, isDark } = useTheme();
  const [drafts, setDrafts] = useState([newDraft()]);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width));
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);
  // Switch to card layout when Name column would be < 80px
  // Fixed cols: 160+196+142+162+182+76 = 918; threshold = 918+80 = 998
  const showCards = containerWidth > 0 && containerWidth < 998;
  const [showMatrix, setShowMatrix] = useState(() => {
    try { return localStorage.getItem('bpp_matrix_open') === 'true'; } catch { return false; }
  });
  const toggleMatrix = () => {
    setShowMatrix(v => {
      const next = !v;
      try { localStorage.setItem('bpp_matrix_open', String(next)); } catch {}
      return next;
    });
  };

  const updateDraft = (id, field, value) =>
    setDrafts(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));

  const removeDraft = (id) =>
    setDrafts(prev => prev.length > 1 ? prev.filter(d => d.id !== id) : [newDraft()]);

  const saveDraft = (draft) => {
    if (!draft.name.trim()) return;
    const item = {
      id: newId(),
      name: draft.name.trim(),
      amount: parseFloat(draft.amount) || 0,
      type: draft.type,
      frequency: draft.frequency,
      lastOccurrence: draft.lastOccurrence || null,
      category: draft.category,
    };
    onItemsChange([...items, item]);
    removeDraft(draft.id);
  };

  const addDraftRow = () => setDrafts(prev => [...prev, newDraft()]);

  const deleteItem = (id) => onItemsChange(items.filter((i) => i.id !== id));

  const allItems = [...items, ...debtPaymentItems];

  // Per-item frequency conversion: itemFreqMatrix[id][freq] = converted amount
  const toFreq = (item, freq) => (item.amount * ANNUAL[item.frequency]) / ANNUAL[freq];

  // Column totals
  const totals = FREQS.reduce((acc, freq) => {
    acc[freq] = allItems.reduce((sum, item) => sum + toFreq(item, freq), 0);
    return acc;
  }, {});

  // Responsive sizing
  const pagePadding = isDesktop ? '24px 32px' : isTablet ? '16px' : '10px';
  const thPadding = isDesktop ? '10px 16px' : isTablet ? '8px 12px' : '6px 8px';
  const tdPadding = isDesktop ? '10px 16px' : isTablet ? '8px 12px' : '6px 8px';
  const thPaddingWide = isDesktop ? '10px 32px' : isTablet ? '8px 24px' : '6px 16px';
  const tdPaddingWide = isDesktop ? '10px 32px' : isTablet ? '8px 24px' : '6px 16px';
  const cellFontSize = isDesktop ? '14px' : isTablet ? '13px' : '12px';
  const addBtnPadding = isDesktop ? '14px 64px' : isTablet ? '12px 40px' : '10px 28px';
  const titleInputPadding = isDesktop ? '16px 20px' : isTablet ? '12px 16px' : '10px 14px';
  const containerMaxWidth = isDesktop ? '1500px' : '1100px';
  const sectionGap = isDesktop ? '20px' : '16px';

  const dynTh = { padding: thPadding, textAlign: 'left', fontWeight: '700', fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary };
  const dynTd = { padding: tdPadding, fontSize: cellFontSize };
  const dynThWide = { ...dynTh, padding: thPaddingWide };
  const dynTdWide = { ...dynTd, padding: tdPaddingWide };
  const dynThType = { ...dynTh, padding: '10px 8px' };
  const dynTdType = { ...dynTd, padding: '10px 8px' };

  // Delete button: card-colored background, red icon, sits in a gap column
  const deleteTd = { padding: '0 0 0 10px', width: '76px', border: 'none', background: 'transparent' };
  const deleteIconBtn = {
    background: t.card,
    border: `1px solid ${t.border}`,
    borderRadius: '8px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#ba1a1a',
    fontSize: '16px',
    fontFamily: 'inherit',
    lineHeight: 1,
    flexShrink: 0,
  };

  // Add button
  const addBtnBg = 'rgb(64, 173, 76)';
  const addBtnTextColor = 'white';

  return (
    <div style={{ ...styles.page, background: t.pageBg, padding: pagePadding }}>
      <div ref={containerRef} style={{ ...styles.container, maxWidth: containerMaxWidth }}>
        {/* Title card */}
        <div style={{ ...styles.card, background: t.card, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Budget title..."
            style={{ ...styles.titleInput, color: title ? t.text : t.textFaint, padding: titleInputPadding, fontSize: isDesktop ? '32px' : isTablet ? '26px' : '22px', fontWeight: '800', letterSpacing: '-0.02em' }}
          />
        </div>

        {/* Input table — wide layout */}
        {!showCards && (
          <div style={{ ...styles.card, background: t.card, marginTop: sectionGap, overflowX: 'auto', overflow: 'visible', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${t.borderLight}`, borderRadius: '12px 12px 0 0' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>Budget Items</span>
              <span style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textMuted }}>Active Items: {items.length}</span>
            </div>
            <table style={{ ...styles.table, tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '160px' }} />
                <col />
                <col style={{ width: '196px' }} />
                <col style={{ width: '142px' }} />
                <col style={{ width: '162px' }} />
                <col style={{ width: '182px' }} />
                <col style={{ width: '76px' }} />
              </colgroup>
              <thead>
                <tr style={{ background: t.tableHeader }}>
                  <th style={{ ...dynThType, borderRadius: '0' }}>Type</th>
                  <th style={dynTh}>Name</th>
                  <th style={dynThWide}>Category</th>
                  <th style={dynThWide}>Amount</th>
                  <th style={dynThWide}>Frequency</th>
                  <th style={dynThWide}>Last Occurred</th>
                  <th style={{ ...dynTh, padding: '10px 6px', textAlign: 'center', border: 'none', background: 'transparent' }}>Ready</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={{ ...dynTdType, textAlign: 'center' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: item.type === 'income' ? '#1b6d24' : '#ba1a1a' }} />
                    </td>
                    <td style={{ ...dynTd, color: t.textSecondary }}>{item.name}</td>
                    <td style={{ ...dynTdWide, color: t.textMuted, fontSize: '12px' }}>
                      {item.category && (
                        <span style={{ background: t.tableHeader, borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: '500', color: t.textSecondary }}>
                          {item.category}
                        </span>
                      )}
                    </td>
                    <td style={{ ...dynTdWide, color: t.textSecondary }}>{fmt(item.amount)}</td>
                    <td style={{ ...dynTdWide, color: t.textSecondary }}>{item.frequency}</td>
                    <td style={{ ...dynTdWide, color: item.lastOccurrence ? t.textSecondary : t.textFaintest, fontSize: cellFontSize }}>
                      {item.lastOccurrence
                        ? new Date(item.lastOccurrence + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </td>
                    <td style={deleteTd}>
                      <button onClick={() => deleteItem(item.id)} style={deleteIconBtn} title="Remove this item">×</button>
                    </td>
                  </tr>
                ))}
                {/* Debt payment rows — read-only */}
                {debtPaymentItems.map((item) => (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${t.border}`, background: 'rgba(154,37,174,0.04)' }}>
                    <td style={dynTdType}></td>
                    <td style={{ ...dynTd, color: t.textSecondary }}>
                      {item.name}
                      <span style={{ marginLeft: '6px', fontSize: '9px', background: '#9a25ae', color: 'white', borderRadius: '20px', padding: '1px 6px', fontWeight: '700', verticalAlign: 'middle' }}>DEBT</span>
                    </td>
                    <td style={dynTdWide}></td>
                    <td style={{ ...dynTdWide, color: t.textSecondary }}>{fmt(item.amount)}</td>
                    <td style={{ ...dynTdWide, color: t.textSecondary }}>{item.frequency}</td>
                    <td style={{ ...dynTdWide, color: t.textFaintest, fontSize: cellFontSize }}>—</td>
                    <td style={deleteTd}></td>
                  </tr>
                ))}
                {/* Draft input rows */}
                {drafts.map((draft) => {
                  const touched = draft.amount !== '' || draft.category !== '';
                  const nameMissing = touched && !draft.name.trim();
                  const amountMissing = touched && draft.name.trim() && draft.amount === '';
                  const isValid = draft.name.trim() !== '';
                  const checkColor = isValid ? (isDark ? '#81c784' : '#1b6d24') : t.textFaintest;
                  const checkTooltip = isValid
                    ? 'This item is ready to be saved — press Enter to add it.'
                    : 'A name is required before this item can be saved.';
                  return (
                    <tr key={draft.id} style={{ background: t.tableInputRow }}>
                      <td style={{ ...dynTdType, textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', justifyContent: 'center' }}>
                          <button onClick={() => setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, type: 'income', category: '' } : d))} style={{ border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', background: draft.type === 'income' ? '#1b6d24' : t.borderMid, color: draft.type === 'income' ? 'white' : t.textMuted }}>Income</button>
                          <button onClick={() => setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, type: 'expense', category: '' } : d))} style={{ border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', background: draft.type === 'expense' ? '#ba1a1a' : t.borderMid, color: draft.type === 'expense' ? 'white' : t.textMuted }}>Expense</button>
                        </div>
                      </td>
                      <td style={dynTd}>
                        <input
                          type="text"
                          value={draft.name}
                          onChange={(e) => updateDraft(draft.id, 'name', e.target.value)}
                          placeholder="Name..."
                          style={{ ...styles.cellInput, color: t.text, borderRadius: '3px', outline: nameMissing ? `1px solid rgba(186,26,26,0.35)` : 'none' }}
                          onKeyDown={(e) => e.key === 'Enter' && saveDraft(draft)}
                          autoFocus={draft === drafts[drafts.length - 1]}
                        />
                      </td>
                      <td style={dynTdWide}>
                        <select
                          value={draft.category}
                          onChange={(e) => updateDraft(draft.id, 'category', e.target.value)}
                          style={{ ...styles.cellInput, cursor: 'pointer', color: t.text, background: t.card, colorScheme: isDark ? 'dark' : 'light' }}
                        >
                          <option value="">Category...</option>
                          {(draft.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </td>
                      <td style={dynTdWide}>
                        <input
                          type="number"
                          value={draft.amount}
                          onChange={(e) => updateDraft(draft.id, 'amount', e.target.value)}
                          placeholder="0.00"
                          style={{ ...styles.cellInput, color: t.text, borderRadius: '3px', outline: amountMissing ? `1px solid rgba(186,26,26,0.35)` : 'none' }}
                          min="0"
                          step="0.01"
                          onKeyDown={(e) => e.key === 'Enter' && saveDraft(draft)}
                        />
                      </td>
                      <td style={dynTdWide}>
                        <select
                          value={draft.frequency}
                          onChange={(e) => updateDraft(draft.id, 'frequency', e.target.value)}
                          style={{ ...styles.cellInput, cursor: 'pointer', color: t.text, background: t.card, colorScheme: isDark ? 'dark' : 'light' }}
                        >
                          {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </td>
                      <td style={dynTdWide}>
                        <input
                          type="date"
                          value={draft.lastOccurrence}
                          onChange={(e) => updateDraft(draft.id, 'lastOccurrence', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                          style={{ ...styles.cellInput, fontSize: cellFontSize, color: t.text, colorScheme: isDark ? 'dark' : 'light' }}
                        />
                      </td>
                      {/* Checkmark indicator (not a button) + remove-draft button outside */}
                      <td style={{ ...deleteTd, verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                          <span
                            title={checkTooltip}
                            style={{ fontSize: '16px', color: checkColor, lineHeight: 1, cursor: 'default', userSelect: 'none' }}
                          >✓</span>
                          {!isValid && (
                            <span style={{ fontSize: '9px', color: t.textFaintest, textAlign: 'center', lineHeight: 1.2 }}>Name<br/>req.</span>
                          )}
                          {drafts.length > 1 && (
                            <button
                              onClick={() => removeDraft(draft.id)}
                              style={deleteIconBtn}
                              title="Remove this row"
                            >×</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Budget item cards — narrow layout */}
        {showCards && (
          <div style={{ marginTop: sectionGap }}>
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: t.card, borderRadius: '12px 12px 0 0', borderBottom: `1px solid ${t.borderLight}`, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <span style={{ fontSize: '15px', fontWeight: '700', color: t.text }}>Budget Items</span>
              <span style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textMuted }}>Active Items: {items.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', background: t.card, borderRadius: '0 0 12px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>

              {/* Existing items */}
              {items.map((item) => (
                <div key={item.id} style={{ border: `1px solid ${t.border}`, borderRadius: '10px', padding: '14px', background: t.card }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: item.type === 'income' ? '#1b6d24' : '#ba1a1a', marginBottom: '3px' }}>
                        {item.type}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: t.text, marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                      </div>
                      {item.category && (
                        <span style={{ background: t.tableHeader, borderRadius: '10px', padding: '2px 8px', fontSize: '11px', fontWeight: '500', color: t.textSecondary }}>
                          {item.category}
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: t.text, fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(item.amount)}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: t.textMuted, marginTop: '2px' }}>
                        {item.frequency}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${t.borderLight}` }}>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary, marginBottom: '2px' }}>Last Occurred</div>
                      <div style={{ fontSize: '13px', color: item.lastOccurrence ? t.textSecondary : t.textFaintest }}>
                        {item.lastOccurrence
                          ? new Date(item.lastOccurrence + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </div>
                    </div>
                    <button onClick={() => deleteItem(item.id)} style={deleteIconBtn} title="Remove this item">×</button>
                  </div>
                </div>
              ))}

              {/* Debt payment cards — read-only */}
              {debtPaymentItems.map((item) => (
                <div key={item.id} style={{ border: `1px solid ${t.border}`, borderRadius: '10px', padding: '14px', background: 'rgba(154,37,174,0.04)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: '#9a25ae', marginBottom: '3px' }}>
                        Debt Payment
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: t.text, fontVariantNumeric: 'tabular-nums' }}>
                        {fmt(item.amount)}
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em', textTransform: 'uppercase', color: t.textMuted, marginTop: '2px' }}>
                        {item.frequency}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Draft input cards */}
              {drafts.map((draft) => {
                const touched = draft.amount !== '' || draft.category !== '';
                const nameMissing = touched && !draft.name.trim();
                const amountMissing = touched && draft.name.trim() && draft.amount === '';
                const isValid = draft.name.trim() !== '';
                const checkColor = isValid ? (isDark ? '#81c784' : '#1b6d24') : t.textFaintest;
                const checkTooltip = isValid
                  ? 'This item is ready to be saved — press Enter to add it.'
                  : 'A name is required before this item can be saved.';
                return (
                  <div key={draft.id} style={{ border: `1px solid ${t.border}`, borderRadius: '10px', padding: '14px', background: t.tableInputRow }}>
                    {/* Type toggle + amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, type: 'income', category: '' } : d))} style={{ border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', background: draft.type === 'income' ? '#1b6d24' : t.borderMid, color: draft.type === 'income' ? 'white' : t.textMuted }}>Income</button>
                        <button onClick={() => setDrafts(prev => prev.map(d => d.id === draft.id ? { ...d, type: 'expense', category: '' } : d))} style={{ border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', background: draft.type === 'expense' ? '#ba1a1a' : t.borderMid, color: draft.type === 'expense' ? 'white' : t.textMuted }}>Expense</button>
                      </div>
                      <input
                        type="number"
                        value={draft.amount}
                        onChange={(e) => updateDraft(draft.id, 'amount', e.target.value)}
                        placeholder="0.00"
                        style={{ ...styles.cellInput, color: t.text, width: '110px', textAlign: 'right', fontSize: '18px', fontWeight: '700', fontVariantNumeric: 'tabular-nums', outline: amountMissing ? `1px solid rgba(186,26,26,0.35)` : 'none', borderRadius: '3px' }}
                        min="0"
                        step="0.01"
                        onKeyDown={(e) => e.key === 'Enter' && saveDraft(draft)}
                      />
                    </div>
                    {/* Name + frequency */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={draft.name}
                        onChange={(e) => updateDraft(draft.id, 'name', e.target.value)}
                        placeholder="Name..."
                        style={{ ...styles.cellInput, color: t.text, fontSize: '15px', fontWeight: '600', flex: 1, outline: nameMissing ? `1px solid rgba(186,26,26,0.35)` : 'none', borderRadius: '3px' }}
                        onKeyDown={(e) => e.key === 'Enter' && saveDraft(draft)}
                        autoFocus={draft === drafts[drafts.length - 1]}
                      />
                      <select
                        value={draft.frequency}
                        onChange={(e) => updateDraft(draft.id, 'frequency', e.target.value)}
                        style={{ ...styles.cellInput, cursor: 'pointer', color: t.text, background: t.card, colorScheme: isDark ? 'dark' : 'light', width: 'auto', flexShrink: 0, fontSize: '12px' }}
                      >
                        {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                    {/* Category */}
                    <select
                      value={draft.category}
                      onChange={(e) => updateDraft(draft.id, 'category', e.target.value)}
                      style={{ ...styles.cellInput, cursor: 'pointer', color: t.text, background: t.card, colorScheme: isDark ? 'dark' : 'light', width: '100%', marginBottom: '10px' }}
                    >
                      <option value="">Category...</option>
                      {(draft.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    {/* Last occurred + actions */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: `1px solid ${t.borderLight}` }}>
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary, marginBottom: '4px' }}>Last Occurred</div>
                        <input
                          type="date"
                          value={draft.lastOccurrence}
                          onChange={(e) => updateDraft(draft.id, 'lastOccurrence', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                          style={{ ...styles.cellInput, fontSize: '13px', color: t.text, colorScheme: isDark ? 'dark' : 'light', width: 'auto' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', marginLeft: '8px' }}>
                        <span title={checkTooltip} style={{ fontSize: '16px', color: checkColor, lineHeight: 1, cursor: 'default', userSelect: 'none' }}>✓</span>
                        {drafts.length > 1 && (
                          <button onClick={() => removeDraft(draft.id)} style={deleteIconBtn} title="Remove this row">×</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

            </div>
          </div>
        )}

        {/* Frequency matrix — each item as a row, each frequency as a column */}
        {allItems.length > 0 && (
          <div style={{ marginTop: sectionGap }}>
            <button
              onClick={toggleMatrix}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '13px', color: t.textMuted, fontFamily: 'inherit',
                padding: '6px 0', display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <span style={{ fontSize: '10px' }}>{showMatrix ? '▼' : '▶'}</span>
              {showMatrix ? 'Hide detailed breakdown' : 'Show detailed breakdown'}
            </button>
            {showMatrix && (
              <div style={{ ...styles.card, background: t.card, overflowX: 'auto', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <table style={{ ...styles.table, minWidth: '640px' }}>
                  <thead>
                    <tr style={{ background: t.tableHeader }}>
                      <th style={{ ...dynTh, minWidth: '140px' }}>Name</th>
                      {FREQS.map((freq) => (
                        <th key={freq} style={{ ...dynTh, textAlign: 'right', whiteSpace: 'nowrap' }}>{freq}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item) => (
                      <tr key={item.id} style={{ borderBottom: `1px solid ${t.border}`, background: item.isDebtPayment ? 'rgba(154,37,174,0.04)' : t.card }}>
                        <td style={{ ...dynTd, color: t.textSecondary }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {!item.isDebtPayment && (
                              <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', background: item.type === 'income' ? '#1b6d24' : '#ba1a1a', flexShrink: 0 }} />
                            )}
                            <span>{item.name}</span>
                            {item.isDebtPayment && (
                              <span style={{ fontSize: '9px', background: '#9a25ae', color: 'white', borderRadius: '20px', padding: '1px 6px', fontWeight: '700' }}>DEBT</span>
                            )}
                          </div>
                        </td>
                        {FREQS.map((freq) => (
                          <td key={freq} style={{ ...dynTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: t.textSub }}>
                            {fmt(toFreq(item, freq))}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr style={{ background: t.border, fontWeight: '700' }}>
                      <td style={{ ...dynTd, fontWeight: '700', fontSize: cellFontSize, color: t.text, letterSpacing: '0.03em' }}>NET CASHFLOW</td>
                      {FREQS.map((freq) => (
                        <td key={freq} style={{ ...dynTd, textAlign: 'right', fontWeight: '700', fontVariantNumeric: 'tabular-nums', color: t.text }}>
                          {fmt(totals[freq])}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Add row button */}
        <div style={{ textAlign: 'center', marginTop: sectionGap }}>
          <button
            onClick={addDraftRow}
            style={{ ...styles.addBtn, padding: addBtnPadding, background: addBtnBg, color: addBtnTextColor }}
          >
            Add Item +
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    padding: '20px',
  },
  container: {
    maxWidth: '1100px',
    margin: '0 auto',
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  blueBtn: {
    background: '#0061a4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 18px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: 'inherit',
  },
  addBtn: {
    border: 'none',
    borderRadius: '24px',
    padding: '14px 64px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    fontFamily: 'inherit',
  },
  titleInput: {
    width: '100%',
    border: 'none',
    outline: 'none',
    fontSize: '20px',
    padding: '16px 20px',
    color: '#888',
    background: 'transparent',
    fontFamily: 'inherit',
  },
  cellInput: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '14px',
    padding: '2px',
    fontFamily: 'inherit',
  },
};
